import {
    type FrontendApi,
    type Session as OrySession,
} from '@ory/client-fetch';
import * as Sentry from '@sentry/react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useOry } from '@apps/web/hooks/ory';
import {
    dispatchSessionEnded,
    SESSION_VALIDATED_EVENT,
    type SessionEndedReason,
    sessionEventBus,
} from '@apps/web/lib/event_bus';
import {
    clearExtensionSessionToken,
    getExtensionSessionToken,
} from '@apps/web/lib/extension_session_token';
import { assert } from '@libraries/typescript/utilities';

type Session = { readonly identity: { readonly id: string } };

export type SessionStateContextType = {
    readonly session: Session | null;
    readonly initialized: boolean;
    // Re-run session validation, e.g. after another endpoint returns a
    // definitive 401. A genuinely dead session falls through to sign-out; a
    // transient failure preserves the current session (same logic as a focus
    // revalidation).
    readonly revalidateSession: () => void;
};

const SessionStateContext = createContext<SessionStateContextType>({
    session: null,
    initialized: false,
    revalidateSession: () => {},
});

// `@ory/client-fetch` throws a `ResponseError` carrying the fetch `Response` on
// a non-2xx; a 401 means the session is genuinely gone/expired. Network or
// otherwise transient failures throw without a 401 status. We only preserve a
// session across *transient* revalidation failures — a definitive 401 must
// still sign the user out. Unknown shapes fall through to `false` (treated as
// transient → preserved), which is the safe direction: no spurious sign-out.
function isUnauthorizedSessionError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const response = (
        error as { readonly response?: { readonly status?: unknown } }
    ).response;
    const status =
        response && typeof response.status === 'number'
            ? response.status
            : (error as { readonly status?: unknown }).status;
    return status === 401;
}

// Retry a *transient* (non-401) `toSession` failure a couple of times before
// giving up. A definitive 401 means the session is genuinely gone, so surface
// it immediately. This closes the cold-load gap that the revalidation guard
// below does not cover: the very first session check on a fresh page load has
// no prior session to fall back on, so a single network blip when an SMS
// notification opens the app cold would otherwise bounce a logged-in user to
// /sign-in.
const SESSION_CHECK_TRANSIENT_RETRIES = 2;
const SESSION_CHECK_RETRY_BASE_DELAY_MS = 200;

export async function fetchSessionWithTransientRetry(
    oryClient: FrontendApi,
    extensionSessionToken: string | null | undefined,
): Promise<OrySession> {
    for (let attempt = 0; ; attempt++) {
        try {
            return extensionSessionToken
                ? await oryClient.toSession({
                      xSessionToken: extensionSessionToken,
                  })
                : await oryClient.toSession();
        } catch (error) {
            if (
                isUnauthorizedSessionError(error) ||
                attempt >= SESSION_CHECK_TRANSIENT_RETRIES
            ) {
                throw error;
            }
            await new Promise<void>((resolve) => {
                setTimeout(
                    resolve,
                    SESSION_CHECK_RETRY_BASE_DELAY_MS * (attempt + 1),
                );
            });
        }
    }
}

export function useSessionState() {
    return useContext(SessionStateContext);
}

export function clearSessionAfterDispatch(
    reason: SessionEndedReason,
    setCurrentSession: (session: null) => void,
): void {
    // Session-end listeners synchronously purge identity-bound state, so they
    // must run while the current identity is still available to those hooks.
    dispatchSessionEnded(reason);
    setCurrentSession(null);
}

export function SessionStateProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [session, setSession] = useState<Session | null>(null);
    const [initialized, setInitialized] = useState(false);
    const { oryClient } = useOry();
    // Mirror `session` so a revalidation can read the current value without
    // re-subscribing `loadSession` (which would re-fire the visibilitychange
    // effect on every session change).
    const sessionRef = useRef<Session | null>(null);
    // Gate focus revalidations until the initial cold load has settled, so a
    // concurrent visibilitychange can't sign out or mark the app initialized
    // before the cold-load retry finishes (which would defeat the retry).
    const initialLoadCompleteRef = useRef(false);
    const setCurrentSession = useCallback((session: Session | null) => {
        sessionRef.current = session;
        setSession(session);
    }, []);

    const loadSession = useCallback(
        async ({ isRevalidation = false } = {}) => {
            // The sidepanel iframe carries its session token in the URL hash
            // because its storage-partitioned frame cannot send the session
            // cookie. Read it synchronously; outside the extension this is null
            // and the normal cookie-based `whoami` runs unchanged. Note that
            // `useOry` reads the same token during render and pins the client to
            // `credentials: 'omit'` whenever it is present, so a request made
            // with a token present carries no cookie.
            const extensionSessionToken = getExtensionSessionToken();

            try {
                const session = await fetchSessionWithTransientRetry(
                    oryClient,
                    extensionSessionToken,
                );
                assert.isNotUndefined(
                    session.identity,
                    'No identity found in session',
                );
                setCurrentSession({
                    identity: { id: session.identity.id },
                });
                // Announced only on a confirmed session, never on the
                // preserve-across-transient-failure path below: a socket that
                // gave up re-arms on this, and re-arming on an unproven
                // credential spends its single remaining probe for nothing.
                sessionEventBus.dispatchEvent(
                    new Event(SESSION_VALIDATED_EVENT),
                );
            } catch (error) {
                if (
                    isRevalidation &&
                    sessionRef.current &&
                    !isUnauthorizedSessionError(error)
                ) {
                    // A *transient* revalidation failure (e.g. a visibilitychange
                    // blip when a deep-link tab opens/focuses) must not null an
                    // already-valid session — that bounces the page to /sign-in.
                    // A definitive 401 is not transient: it falls through to
                    // setSession(null) below and signs the user out.
                    //
                    // The sidepanel token is deliberately kept on this path. It
                    // arrives in a URL hash that is stripped on first read, so
                    // nothing re-supplies it for the frame's lifetime, and the
                    // websocket needs it to mint a ws ticket. Dropping it while
                    // keeping the session left a live-looking panel over a dead
                    // realtime feed until the user reloaded.
                    return;
                }

                if (!isUnauthorizedSessionError(error)) {
                    // Signing out on a NON-401 failure — a transient error that
                    // survived retries (or a cold load with no prior session to
                    // preserve). This is the spurious-logout case we were
                    // previously blind to; capture the actual error (status,
                    // type, stack) so we can diagnose it in prod.
                    Sentry.captureException(error, { level: 'warning' });
                }
                // Tied to the sign-out rather than to the failure: the token is
                // this session's credential, so it outlives the session on no
                // path. Clearing it here also re-derives `useOry`'s client with
                // `credentials: 'include'` on the next render, which re-fires
                // the mount effect below as a cookie-capable retry — the only
                // path on which a cookie can be tried at all.
                if (extensionSessionToken) {
                    clearExtensionSessionToken();
                }
                clearSessionAfterDispatch('session_loss', setCurrentSession);
            } finally {
                initialLoadCompleteRef.current = true;
                setInitialized(true);
            }
        },
        [oryClient, setCurrentSession],
    );

    const revalidateSession = useCallback(() => {
        void loadSession({ isRevalidation: true });
    }, [loadSession]);

    useEffect(() => {
        void loadSession();
    }, [loadSession]);

    useEffect(() => {
        const revalidate = () => {
            // Ignore focus revalidations until the initial cold load settles —
            // a concurrent one could otherwise initialize / sign out before the
            // cold-load retry completes and bounce the page to /sign-in.
            if (!initialLoadCompleteRef.current) {
                return;
            }
            void loadSession({ isRevalidation: true });
        };
        window.addEventListener('visibilitychange', revalidate);
        return () => {
            window.removeEventListener('visibilitychange', revalidate);
        };
    }, [loadSession]);

    return (
        <SessionStateContext.Provider
            value={{ session, initialized, revalidateSession }}
        >
            {children}
        </SessionStateContext.Provider>
    );
}
